import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StatusBadge from '../../components/shared/StatusBadge';
import PipelineStage from '../../components/shared/PipelineStage';
import {
  PlusIcon, XMarkIcon, StarIcon, CalendarDaysIcon,
  CheckBadgeIcon, ClipboardDocumentListIcon,
  UserPlusIcon, EyeIcon,
} from '@heroicons/react/24/outline';
import { DATE_FLOOR } from '../../lib/dateBounds';

const stages = [
  'application_received', 'pre_interview', 'interview_scheduled',
  'interview_conducted', 'hire_decision', 'offer_letter',
  'hired', 'no_hire', 'talent_pool'
];

const roleOptions = ['admin', 'teacher', 'float', 'cook'];
const formatRole = (r) => ({
  admin: 'Office Admin',
  teacher: 'Full-Time Teacher',
  float: 'Part-Time Float',
  cook: 'Cook',
}[r] || r);
const formatStage = (s) => s?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export default function RecruitmentPage() {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(null);
  const [showInterviewModal, setShowInterviewModal] = useState(null);
  const [filterStage, setFilterStage] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const { register, handleSubmit, reset } = useForm();
  const decisionForm = useForm();
  const interviewForm = useForm();

  useEffect(() => { loadCandidates(); }, [filterStage, filterRole]);

  const loadCandidates = async () => {
    try {
      const params = {};
      if (filterStage) params.stage = filterStage;
      if (filterRole) params.role_applied = filterRole;
      const { data } = await api.get('/recruitment/candidates', { params });
      setCandidates(data);
    } catch { toast.error('Failed to load candidates'); }
    finally { setLoading(false); }
  };

  const openCandidate = (id) => navigate(`/recruitment/candidates/${id}`);

  // ─── Add Candidate ─────────────────────────────────────────────
  const onSubmit = async (formData) => {
    try {
      const { data } = await api.post('/recruitment/candidates', formData);
      toast.success('Candidate added');
      setShowForm(false); reset(); loadCandidates();
      // Drop straight into the detail page so they can flesh things out
      navigate(`/recruitment/candidates/${data.id}`);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save'); }
  };

  // ─── Send Assessment Link ──────────────────────────────────────
  // Rotates the candidate's application_token, then re-sends the
  // "Continue your application" email (attestation → assessment).
  const sendSurvey = async (candidateId, alreadySent) => {
    if (alreadySent && !window.confirm(
      'Already sent. Send again? A fresh link will be generated and any older link will stop working.'
    )) return;
    setActionLoading(true);
    try {
      await api.post(`/recruitment/candidates/${candidateId}/send-survey`);
      toast.success(alreadySent ? 'Fresh assessment link sent' : 'Assessment link sent');
      loadCandidates();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to send link'); }
    finally { setActionLoading(false); }
  };

  // ─── Schedule Interview ────────────────────────────────────────
  const submitInterview = async (formData) => {
    setActionLoading(true);
    try {
      await api.post(`/recruitment/candidates/${showInterviewModal.id}/schedule-interview`, formData);
      toast.success('Interview scheduled & email sent!');
      setShowInterviewModal(null);
      interviewForm.reset();
      loadCandidates();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to schedule'); }
    finally { setActionLoading(false); }
  };

  // ─── Make Decision ─────────────────────────────────────────────
  const submitDecision = async (formData) => {
    setActionLoading(true);
    try {
      const { data } = await api.post(`/recruitment/candidates/${showDecisionModal.id}/decision`, formData);
      if (data.tempPassword) {
        toast.success(`Hired! Temp password: ${data.tempPassword}`, { duration: 10000 });
      } else {
        toast.success(`Decision: ${formData.decision.replace(/_/g, ' ')}`);
      }
      setShowDecisionModal(null);
      decisionForm.reset();
      loadCandidates();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to submit decision'); }
    finally { setActionLoading(false); }
  };

  const decisionValue = decisionForm.watch('decision');

  // ─── Stage Count Badges ────────────────────────────────────────
  const stageCounts = stages.reduce((acc, s) => {
    acc[s] = candidates.filter(c => c.stage === s).length;
    return acc;
  }, {});

  return (
    <div className="animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recruitment Pipeline</h1>
          <p className="text-gray-500 text-sm">{candidates.length} candidate{candidates.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => { reset(); setShowForm(true); }} className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-xl hover:bg-orange-600 transition-colors font-medium shadow-lg shadow-orange-200/50">
          <PlusIcon className="w-5 h-5" /> Add Candidate
        </button>
      </div>

      {/* Stage Pipeline Overview */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        <button onClick={() => setFilterStage('')}
          className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${!filterStage ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          All ({candidates.length})
        </button>
        {stages.map(s => (
          <button key={s} onClick={() => setFilterStage(s)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterStage === s ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {formatStage(s)} {stageCounts[s] > 0 && <span className="ml-1 bg-white/20 px-1.5 rounded-full">{stageCounts[s]}</span>}
          </button>
        ))}
      </div>

      {/* Role Filter */}
      <div className="flex gap-3 mb-6">
        <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none">
          <option value="">All Roles</option>
          {roleOptions.map(r => <option key={r} value={r}>{formatRole(r)}</option>)}
        </select>
        <button onClick={() => { setFilterStage(''); setFilterRole(''); }} className="text-sm text-gray-500 hover:text-orange-500">Clear filters</button>
      </div>

      {/* Candidate Table */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      ) : candidates.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <p className="text-gray-500">No candidates found</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Candidate</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Role</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Stage</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Survey</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Availability</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Interview</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Score</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {candidates.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => openCandidate(c.id)}>
                  <td className="px-6 py-4">
                    <p className="font-medium text-gray-900">{c.first_name} {c.last_name}</p>
                    <p className="text-xs text-gray-500">{c.email}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {c.source === 'public_application' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-violet-100 text-violet-700">
                          <UserPlusIcon className="w-3 h-3" /> Applied Online
                        </span>
                      )}
                      {c.source_detail && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                          {c.source_detail}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{formatRole(c.role_applied)}</td>
                  <td className="px-6 py-4"><PipelineStage stage={c.pipeline_stage} /></td>
                  <td className="px-6 py-4">
                    {c.survey_completed_at ? (
                      <span className="text-xs text-green-600 font-medium">Completed</span>
                    ) : c.survey_sent_at ? (
                      <span className="text-xs text-amber-600 font-medium">Sent</span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {c.availability_days ? (
                      <span className="text-xs">{c.availability_days.split(',').map(d => d.slice(0, 3)).join(', ')}</span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {c.interview_date ? new Date(c.interview_date).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-6 py-4">
                    {c.interview_score ? (
                      <div className="flex items-center gap-1">
                        <StarIcon className="w-4 h-4 text-amber-500" />
                        <span className="text-sm font-medium">{c.interview_score}/5</span>
                      </div>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-2">
                      {!c.survey_completed_at && !['hired', 'no_hire', 'talent_pool'].includes(c.stage) && (
                        <button onClick={() => sendSurvey(c.id, !!c.survey_sent_at)} disabled={actionLoading}
                          className="text-xs bg-blue-50 text-blue-600 px-2.5 py-1 rounded-lg hover:bg-blue-100 font-medium"
                          title={c.survey_sent_at ? `Last sent ${new Date(c.survey_sent_at).toLocaleDateString()}` : 'Send the attestation + assessment link'}>
                          <ClipboardDocumentListIcon className="w-3.5 h-3.5 inline mr-1" />
                          {c.survey_sent_at ? 'Re-send' : 'Send assessment'}
                        </button>
                      )}
                      {['application_received', 'pre_interview'].includes(c.stage) && (
                        <button onClick={() => { setShowInterviewModal(c); interviewForm.reset(); }}
                          className="text-xs bg-purple-50 text-purple-600 px-2.5 py-1 rounded-lg hover:bg-purple-100 font-medium">
                          <CalendarDaysIcon className="w-3.5 h-3.5 inline mr-1" />Interview
                        </button>
                      )}
                      {['interview_scheduled', 'interview_conducted', 'hire_decision'].includes(c.stage) && (
                        <button onClick={() => { setShowDecisionModal(c); decisionForm.reset(); }}
                          className="text-xs bg-green-50 text-green-600 px-2.5 py-1 rounded-lg hover:bg-green-100 font-medium">
                          <CheckBadgeIcon className="w-3.5 h-3.5 inline mr-1" />Decision
                        </button>
                      )}
                      <button onClick={() => openCandidate(c.id)}
                        className="text-xs text-gray-500 hover:text-orange-500">
                        <EyeIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* Add Candidate Form Modal */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold">Add Candidate</h2>
              <button onClick={() => { setShowForm(false); reset(); }}><XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input {...register('first_name', { required: true })} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input {...register('last_name', { required: true })} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" {...register('email', { required: true })} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input {...register('phone', { required: true })} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role Applied *</label>
                <select {...register('role_applied', { required: true })} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none bg-white">
                  <option value="">Select role</option>
                  {roleOptions.map(r => <option key={r} value={r}>{formatRole(r)}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                  <input type="date" min={DATE_FLOOR} {...register('date_of_birth', { required: true })}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State of Birth *</label>
                  <input {...register('state_of_birth', { required: true, maxLength: 2, minLength: 2 })}
                    maxLength={2} placeholder="IN"
                    className="w-full uppercase rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
                </div>
              </div>
              <p className="text-[11px] text-gray-500 -mt-2">
                DOB + state of birth are required for iLEAD consent, background check, and drug screen scheduling.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <select {...register('source_detail')} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 bg-white focus:border-orange-500 focus:outline-none">
                    <option value="">—</option>
                    <option>Indeed</option><option>Facebook</option><option>Walk-in</option>
                    <option>Referral - Employee</option><option>Referral - Parent</option>
                    <option>Google</option><option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referred By</label>
                  <input {...register('referred_by')} placeholder="Name (if referral)" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Availability (days)</label>
                <input {...register('availability_days')} placeholder="monday,tuesday,wednesday" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hours</label>
                  <input {...register('availability_hours')} placeholder="7am-3pm" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Earliest Start</label>
                  <input type="date" min={DATE_FLOOR} {...register('earliest_start_date')} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="flex-1 bg-orange-500 text-white font-semibold py-2.5 rounded-xl hover:bg-orange-600 transition-colors">Add Candidate</button>
                <button type="button" onClick={() => { setShowForm(false); reset(); }} className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* Schedule Interview Modal */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {showInterviewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold">Schedule Interview</h2>
              <button onClick={() => setShowInterviewModal(null)}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
            </div>
            <form onSubmit={interviewForm.handleSubmit(submitInterview)} className="p-6 space-y-4">
              <p className="text-sm text-gray-600">For <strong>{showInterviewModal.first_name} {showInterviewModal.last_name}</strong></p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date & Time *</label>
                <input type="datetime-local" {...interviewForm.register('interview_date', { required: true })} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zoom / Meeting Link</label>
                <input {...interviewForm.register('zoom_link')} placeholder="https://zoom.us/j/..." className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea {...interviewForm.register('notes')} rows={2} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none resize-none" />
              </div>
              <p className="text-xs text-gray-500">An email will be sent to the candidate with the interview details.</p>
              <div className="flex gap-3">
                <button type="submit" disabled={actionLoading} className="flex-1 bg-purple-600 text-white font-semibold py-2.5 rounded-xl hover:bg-purple-700 disabled:opacity-50 transition-colors">
                  {actionLoading ? 'Scheduling...' : 'Schedule & Send Email'}
                </button>
                <button type="button" onClick={() => setShowInterviewModal(null)} className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* Hire Decision Modal */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {showDecisionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold">Hire Decision</h2>
              <button onClick={() => setShowDecisionModal(null)}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
            </div>
            <form onSubmit={decisionForm.handleSubmit(submitDecision)} className="p-6 space-y-4">
              <p className="text-sm text-gray-600">For <strong>{showDecisionModal.first_name} {showDecisionModal.last_name}</strong></p>

              {/* "Hire" is intentionally NOT here — hiring goes through
                  Move Forward on the candidate's Offer tab, which collects
                  pay rate / start date / responsibilities and generates the
                  conditional offer letter. This modal is only for the
                  non-hire outcomes. */}
              <div className="grid grid-cols-1 gap-3">
                {[
                  { val: 'no_hire', label: 'Not Selected', color: 'red', desc: 'Send rejection email' },
                  { val: 'talent_pool', label: 'Talent Pool', color: 'blue', desc: 'Interested, no position right now' },
                  { val: 'no_show', label: 'No Show', color: 'amber', desc: 'Missed interview' },
                ].map(opt => (
                  <label key={opt.val}
                    className={`border-2 rounded-xl p-3 cursor-pointer transition-all ${decisionValue === opt.val ? `border-${opt.color}-500 bg-${opt.color}-50` : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" {...decisionForm.register('decision', { required: true })} value={opt.val} className="sr-only" />
                    <p className="text-sm font-semibold text-gray-900">{opt.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                  </label>
                ))}
              </div>

              <div className="bg-green-50 border border-green-100 rounded-xl p-3">
                <p className="text-xs text-green-800">
                  <strong>Hiring someone?</strong> Close this and use <strong>Move Forward</strong> on the candidate's Offer / Negotiate tab — that collects pay rate, start date, and responsibilities, then generates the conditional offer letter.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea {...decisionForm.register('notes')} rows={2} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none resize-none" />
              </div>

              <div className="bg-amber-50 rounded-xl p-3">
                <p className="text-xs text-amber-700">
                  {decisionValue ? 'An email notification will be sent to the candidate.' : 'Select a decision above.'}
                </p>
              </div>

              <div className="flex gap-3">
                <button type="submit" disabled={actionLoading || !decisionValue}
                  className="flex-1 bg-orange-500 text-white font-semibold py-2.5 rounded-xl hover:bg-orange-600 disabled:opacity-50 transition-colors">
                  {actionLoading ? 'Processing...' : 'Confirm Decision'}
                </button>
                <button type="button" onClick={() => setShowDecisionModal(null)} className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
